import React, { useState } from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';

import { MessageBannerVariants } from '../definitions/MessageBannerDefinitions';
import MAGIC_LINK_VARIANTS from '../definitions/MagicLinkVariants';
import MagicLinkSignInMessagesPropType from '../definitions/MagicLinkSignInMessagesPropType';
import Colours from '../definitions/Colours';

import trackHeapEvent from '../functions/trackHeapEvent';
import { requestMagicLink } from '../functions/requestMagicLink';
import getDashboardBasePath from '../functions/getDashboardBasePath';

import useSession from '../hooks/useSessionStore';

import useIsMounted from '../hooks/useIsMounted';
import MessageBanner from './MessageBanner';
import MagicLinkSignIn from './MagicLinkSignIn';
import MagicLinkConfirmationScreen from './MagicLinkConfirmationScreen';
import CircleSpinner from './CircleSpinner';
import ModalContainer from './ModalContainer';


const MAGIC_LINK_SIGN_IN_SCREEN = 'SignIn';
const MAGIC_LINK_CONFIRMATION_SCREEN = 'Confirmation';

function MagicLinkForms({
    supportPhone,
    messages = {},
    resumeApplicationLink,
    documentOptions = {},
    magicLinkVariant,
    shouldShowGoogleSignIn = true,
    isOpen,
    onClose,
    children,
    ...otherProps
}) {
    const sessionStore = useSession();
    const intl = useIntl();

    const [ activeScreen, setActiveScreen ] = useState(MAGIC_LINK_SIGN_IN_SCREEN);
    const [ backgroundColor, setBackgroundColor ] = useState(Colours.BLUEBERRY_LIGHTEST);

    const {
        email,
        dispatchMagicLinkRequest,
        isRequestingMagicLink,
        magicLinkError,
    } = useMagicLink({
        resumeApplicationLink,
        magicLinkVariant,
        documentOptions,
    });

    async function handleMagicLinkSignIn(signInEmail) {
        if (await dispatchMagicLinkRequest(signInEmail)) {
            if (shouldShowGoogleSignIn) {
                trackHeapEvent('[GoogleSSOExperiment] MagicLink requested', {
                    loginSource: 'MagicLink',
                });
            }

            setActiveScreen(MAGIC_LINK_CONFIRMATION_SCREEN);
            setBackgroundColor(Colours.COCONUT);
        }
    }

    async function handleGoogleSignIn(providerToken) {
        const { document, documentType, documentRedirect } = documentOptions;

        // same with authorize page
        const resumeLink = resumeApplicationLink
            ?? documentRedirect
            ?? `${getDashboardBasePath(intl.locale)}`;

        await sessionStore.signInBySocialService({
            providerToken,
            redirectTo: resumeLink,
            document,
            documentType,
        });
    }

    function handleClose() {
        setActiveScreen(MAGIC_LINK_SIGN_IN_SCREEN);
        setBackgroundColor(Colours.BLUEBERRY_LIGHTEST);
        onClose?.();
    }

    return (
        <ModalContainer
            isOpen={isOpen}
            onClose={handleClose}
            showBranding={true}
            contentMaxWidth="1440px"
            style={{
                content: {
                    backgroundColor,
                },
            }}
            {...otherProps}
        >
            <Container className="rh-height-100p rh-display-flex rh-flex-direction-column rh-align-items-center rh-justify-content-center">
                <If condition={magicLinkError}>
                    <MessageBanner
                        iconKey="exclamation-mark"
                        variant={MessageBannerVariants.ERROR}
                        message={(
                            <FormattedMessage
                                id="Accounts.MagicLinkForms.ErrorMessage"
                                defaultMessage={`{activeScreen, select,
                                    SignIn {There has been a problem signing you in. Please try again later.}
                                    Confirmation {There has been a problem resending the email. Please try again later.}
                                    other {}
                                }`}
                                values={{ activeScreen }}
                            />
                        )}
                        data-name="magicLinkForm-errorBanner"
                    />
                </If>

                <If condition={isRequestingMagicLink}>
                    <CircleSpinner
                        position="fixed"
                        callerName="MagicLinkForms"
                    />
                </If>

                <Choose>
                    <When condition={activeScreen === MAGIC_LINK_SIGN_IN_SCREEN}>
                        <div className="sign-in rh-bg-coconut rh-px-1_5 rh-pb-2_5 rh-pt-1_5 rh-mx-auto rh-border-radius-8px rh-b-1px-solid-stone rh-box-sizing-border-box">
                            <MagicLinkSignIn
                                email={email}
                                onSubmit={handleMagicLinkSignIn}
                                messages={messages[MAGIC_LINK_SIGN_IN_SCREEN]}
                                shouldShowGoogleSignIn={shouldShowGoogleSignIn}
                                onGoogleSignIn={handleGoogleSignIn}
                            >
                                {children}
                            </MagicLinkSignIn>
                        </div>
                    </When>

                    <When condition={activeScreen === MAGIC_LINK_CONFIRMATION_SCREEN}>
                        <div className="rh-mx-auto">
                            <MagicLinkConfirmationScreen
                                email={email}
                                supportPhone={supportPhone}
                                messages={messages[MAGIC_LINK_CONFIRMATION_SCREEN]}
                            />
                        </div>
                    </When>
                </Choose>
            </Container>
        </ModalContainer>
    );
}

MagicLinkForms.propTypes = {
    supportPhone: PropTypes.string,
    messages: PropTypes.shape({
        [MAGIC_LINK_SIGN_IN_SCREEN]: MagicLinkSignInMessagesPropType,
        [MAGIC_LINK_CONFIRMATION_SCREEN]: PropTypes.object,
    }),
    magicLinkVariant: PropTypes.oneOf(Object.values(MAGIC_LINK_VARIANTS)),
    resumeApplicationLink: PropTypes.string,
    documentOptions: PropTypes.shape({
        document: PropTypes.object,
        documentType: PropTypes.string,
        documentRedirect: PropTypes.string,
    }),
    shouldShowGoogleSignIn: PropTypes.bool,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    children: PropTypes.node,
};

function useMagicLink({
    magicLinkVariant,
    resumeApplicationLink,
    documentOptions,
}) {
    const getIsMounted = useIsMounted();
    const [ email, setEmail ] = useState(null);
    const [ isRequestingMagicLink, setRequestingMagicLink ] = useState(false);
    const [ magicLinkError, setMagicLinkError ] = useState(null);

    const queryParams = {};
    if (resumeApplicationLink != null) {
        queryParams.redirect = resumeApplicationLink;
    }

    async function dispatchMagicLinkRequest(requestedEmail) {
        let requestError = null; // setState is async

        if (!getIsMounted()) {
            return false;
        }

        setRequestingMagicLink(true);
        setMagicLinkError(null);
        setEmail(requestedEmail);

        try {
            await requestMagicLink({
                email: requestedEmail,
                queryParams,
                documentOptions,
                variant: magicLinkVariant,
            });
        } catch (error) {
            requestError = error;

            if (!getIsMounted()) {
                return false;
            }

            setMagicLinkError(error);

            // eslint-disable-next-line no-console
            console.error(`[useMagicLink]: error requesting magic link: ${error.message}`, error);
        }

        if (!getIsMounted()) {
            return false;
        }

        setRequestingMagicLink(false);

        return requestError == null;
    }

    return {
        // state
        email,
        isRequestingMagicLink,
        magicLinkError,
        // methods
        dispatchMagicLinkRequest,
    };
}

const Container = styled.div`
    height: max(
        calc(100lvh - 8rem), /* 8rem is to account 2x the height of the navbar for both top and bottom spacing*/
        520px
    );
    
    > .sign-in {
        max-width: 530px;
    }
`;

export default observer(MagicLinkForms);

export { MAGIC_LINK_SIGN_IN_SCREEN, MAGIC_LINK_CONFIRMATION_SCREEN };
